import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';

const PortionSelector = ({ onComplete, onCancel }) => {
  const [contentBounds, setContentBounds] = useState({
    x: 0,
    y: 0,
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [selectionBox, setSelectionBox] = useState({
    x: window.innerWidth * 0.25,
    y: window.innerHeight * 0.25,
    width: window.innerWidth * 0.5,
    height: window.innerHeight * 0.5
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const overlayRef = useRef(null);

  // Function to detect actual content bounds
  const detectContentBounds = () => {
    // Try to find the main content area by looking for common patterns
    const selectors = [
      'main',
      '.app',
      '.main-content',
      '[class*="flow"]',
      '[class*="canvas"]',
      'body > div:first-child', // Common app wrapper
      '#root',
      '#app',
      '[data-reactroot]'
    ];

    let contentElement = null;

    // Try each selector until we find a meaningful content area
    for (const selector of selectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        // Choose the largest visible element
        let largestElement = null;
        let largestArea = 0;

        elements.forEach(el => {
          const rect = el.getBoundingClientRect();
          const area = rect.width * rect.height;
          if (area > largestArea && rect.width > 200 && rect.height > 200) {
            largestElement = el;
            largestArea = area;
          }
        });

        if (largestElement) {
          contentElement = largestElement;
          break;
        }
      }
    }

    // Fallback: use document.body if no specific content area found
    if (!contentElement) {
      contentElement = document.body;
    }

    const rect = contentElement.getBoundingClientRect();

    // Ensure we don't go beyond viewport boundaries
    const bounds = {
      x: Math.max(0, rect.left),
      y: Math.max(0, rect.top),
      width: Math.min(rect.width, window.innerWidth - Math.max(0, rect.left)),
      height: Math.min(rect.height, window.innerHeight - Math.max(0, rect.top))
    };

    return bounds;
  };

  // Initialize content bounds and update selection box
  useEffect(() => {
    document.body.style.overflow = 'hidden';

    // Detect actual content bounds
    const updateContentBounds = () => {
      const bounds = detectContentBounds();
      setContentBounds(bounds);

      // Update selection box to be within content bounds if it's the initial setup
      if (selectionBox.x === window.innerWidth * 0.25) {
        const selectionWidth = bounds.width * 0.8;
        const selectionHeight = bounds.height * 0.8;
        setSelectionBox({
          x: bounds.x + (bounds.width - selectionWidth) / 2,
          y: bounds.y + (bounds.height - selectionHeight) / 2,
          width: selectionWidth,
          height: selectionHeight
        });
      }
    };

    // Initial setup
    updateContentBounds();

    // Add resize listener to update bounds when window changes
    const handleResize = () => {
      updateContentBounds();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const rect = overlayRef.current.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // Check if clicking on resize handles
    const handle = getResizeHandle(x, y);
    if (handle) {
      setIsResizing(true);
      setResizeHandle(handle);
      setDragStart({ x, y });
      return;
    }

    // Check if clicking inside selection box for dragging
    if (
      x >= selectionBox.x &&
      x <= selectionBox.x + selectionBox.width &&
      y >= selectionBox.y &&
      y <= selectionBox.y + selectionBox.height
    ) {
      setIsDragging(true);
      setDragStart({
        x: x - selectionBox.x,
        y: y - selectionBox.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging && !isResizing) return;

    const x = e.clientX;
    const y = e.clientY;

    if (isDragging) {
      const newX = Math.max(contentBounds.x, Math.min(contentBounds.x + contentBounds.width - selectionBox.width, x - dragStart.x));
      const newY = Math.max(contentBounds.y, Math.min(contentBounds.y + contentBounds.height - selectionBox.height, y - dragStart.y));

      setSelectionBox(prev => ({
        ...prev,
        x: newX,
        y: newY
      }));
    } else if (isResizing && resizeHandle) {
      const deltaX = x - dragStart.x;
      const deltaY = y - dragStart.y;

      let newBox = { ...selectionBox };

      switch (resizeHandle) {
        case 'nw':
          newBox.x = Math.max(0, selectionBox.x + deltaX);
          newBox.y = Math.max(0, selectionBox.y + deltaY);
          newBox.width = selectionBox.width - deltaX;
          newBox.height = selectionBox.height - deltaY;
          break;
        case 'ne':
          newBox.y = Math.max(0, selectionBox.y + deltaY);
          newBox.width = selectionBox.width + deltaX;
          newBox.height = selectionBox.height - deltaY;
          break;
        case 'sw':
          newBox.x = Math.max(0, selectionBox.x + deltaX);
          newBox.width = selectionBox.width - deltaX;
          newBox.height = selectionBox.height + deltaY;
          break;
        case 'se':
          newBox.width = selectionBox.width + deltaX;
          newBox.height = selectionBox.height + deltaY;
          break;
      }

      // Ensure minimum size
      newBox.width = Math.max(100, newBox.width);
      newBox.height = Math.max(100, newBox.height);

      // Ensure within content bounds
      newBox.x = Math.max(contentBounds.x, Math.min(contentBounds.x + contentBounds.width - newBox.width, newBox.x));
      newBox.y = Math.max(contentBounds.y, Math.min(contentBounds.y + contentBounds.height - newBox.height, newBox.y));

      setSelectionBox(newBox);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeHandle(null);
  };

  const getResizeHandle = (x, y) => {
    const handleSize = 12;
    const handles = [
      { x: selectionBox.x, y: selectionBox.y, handle: 'nw' },
      { x: selectionBox.x + selectionBox.width, y: selectionBox.y, handle: 'ne' },
      { x: selectionBox.x, y: selectionBox.y + selectionBox.height, handle: 'sw' },
      { x: selectionBox.x + selectionBox.width, y: selectionBox.y + selectionBox.height, handle: 'se' }
    ];

    for (const h of handles) {
      if (
        x >= h.x - handleSize / 2 &&
        x <= h.x + handleSize / 2 &&
        y >= h.y - handleSize / 2 &&
        y <= h.y + handleSize / 2
      ) {
        return h.handle;
      }
    }
    return null;
  };

  const getCursor = (x, y) => {
    if (isCapturing) return 'wait';

    const handle = getResizeHandle(x, y);
    if (handle) {
      return `${handle}-resize`;
    }

    if (
      x >= selectionBox.x &&
      x <= selectionBox.x + selectionBox.width &&
      y >= selectionBox.y &&
      y <= selectionBox.y + selectionBox.height
    ) {
      return isDragging ? 'grabbing' : 'grab';
    }

    return 'default';
  };

  const handleCapture = async () => {
    if (isCapturing) return;

    setIsCapturing(true);

    try {
      // Hide the overlay temporarily
      const overlay = overlayRef.current;
      overlay.style.display = 'none';

      // Wait a moment for the overlay to disappear
      await new Promise(resolve => setTimeout(resolve, 200));

      // Use native browser screen capture API for pixel-perfect results
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false,
        selfBrowserSurface: "include",
        surfaceSwitching: "include",
        systemAudio: "exclude"
      });

      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;

      video.onloadedmetadata = () => {
        video.play().then(() => {
          setTimeout(() => {
            // Capture the full screen
            const fullCanvas = document.createElement('canvas');
            fullCanvas.width = video.videoWidth;
            fullCanvas.height = video.videoHeight;

            const fullCtx = fullCanvas.getContext('2d');
            fullCtx.drawImage(video, 0, 0);

            // Calculate the scaling factors between video resolution and content bounds
            const scaleX = video.videoWidth / window.innerWidth;
            const scaleY = video.videoHeight / window.innerHeight;

            // Create a new canvas for the selected portion
            const selectedCanvas = document.createElement('canvas');
            const ctx = selectedCanvas.getContext('2d');

            // Set the size of the selected area (scaled)
            const scaledWidth = selectionBox.width * scaleX;
            const scaledHeight = selectionBox.height * scaleY;
            const scaledX = selectionBox.x * scaleX;
            const scaledY = selectionBox.y * scaleY;

            selectedCanvas.width = scaledWidth;
            selectedCanvas.height = scaledHeight;

            // Draw the selected portion
            ctx.drawImage(
              fullCanvas,
              scaledX, scaledY, scaledWidth, scaledHeight,
              0, 0, scaledWidth, scaledHeight
            );

            // Clean up
            stream.getTracks().forEach(track => track.stop());

            // Convert to data URL
            const dataUrl = selectedCanvas.toDataURL('image/png');
            onComplete(dataUrl);
          }, 300);
        }).catch(err => {
          console.error('Video play error:', err);
          alert('Failed to capture screen. Please try again.');
          stream.getTracks().forEach(track => track.stop());
          setIsCapturing(false);
        });
      };

      video.onerror = (err) => {
        console.error('Video error:', err);
        alert('Failed to capture screen. Please try again.');
        stream.getTracks().forEach(track => track.stop());
        setIsCapturing(false);
      };

    } catch (error) {
      console.error('Screenshot capture failed:', error);
      if (error.name === 'NotAllowedError') {
        alert('Screen capture permission denied. Please allow screen sharing to capture screenshots.');
      } else {
        alert('Failed to capture screenshot. Please try again.');
      }
      setIsCapturing(false);
    }
  };

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[99998] bg-black bg-opacity-50"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{
        cursor: getCursor(0, 0)
      }}
    >
      {/* Selection Box */}
      <div
        className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-10"
        style={{
          left: selectionBox.x,
          top: selectionBox.y,
          width: selectionBox.width,
          height: selectionBox.height,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
      >
        {/* Resize Handles */}
        {!isCapturing && (
          <>
            {/* Corner handles */}
            <div className="absolute w-3 h-3 bg-blue-500 border border-white -top-1.5 -left-1.5 cursor-nw-resize" />
            <div className="absolute w-3 h-3 bg-blue-500 border border-white -top-1.5 -right-1.5 cursor-ne-resize" />
            <div className="absolute w-3 h-3 bg-blue-500 border border-white -bottom-1.5 -left-1.5 cursor-sw-resize" />
            <div className="absolute w-3 h-3 bg-blue-500 border border-white -bottom-1.5 -right-1.5 cursor-se-resize" />
          </>
        )}
      </div>

      {/* Content area overlay with cutout for selection */}
      <div
        className="absolute bg-black bg-opacity-40"
        style={{
          left: contentBounds.x,
          top: contentBounds.y,
          width: contentBounds.width,
          height: contentBounds.height,
          clipPath: `polygon(0 0, 0 100%, ${selectionBox.x - contentBounds.x}px 100%, ${
            selectionBox.x - contentBounds.x
          }px ${selectionBox.y - contentBounds.y}px, ${selectionBox.x + selectionBox.width - contentBounds.x}px ${
            selectionBox.y - contentBounds.y
          }px, ${selectionBox.x + selectionBox.width - contentBounds.x}px ${
            selectionBox.y + selectionBox.height - contentBounds.y
          }px, ${selectionBox.x - contentBounds.x}px ${selectionBox.y + selectionBox.height - contentBounds.y}px, ${
            selectionBox.x - contentBounds.x
          }px 100%, 100% 100%, 100% 0)`,
        }}
      />

      {/* Full window overlay outside content bounds */}
      {/* Top area above content */}
      {contentBounds.y > 0 && (
        <div
          className="absolute bg-black bg-opacity-60"
          style={{
            left: 0,
            top: 0,
            width: '100%',
            height: contentBounds.y
          }}
        />
      )}
      {/* Bottom area below content */}
      {contentBounds.y + contentBounds.height < window.innerHeight && (
        <div
          className="absolute bg-black bg-opacity-60"
          style={{
            left: 0,
            top: contentBounds.y + contentBounds.height,
            width: '100%',
            height: window.innerHeight - (contentBounds.y + contentBounds.height)
          }}
        />
      )}
      {/* Left area beside content */}
      {contentBounds.x > 0 && (
        <div
          className="absolute bg-black bg-opacity-60"
          style={{
            left: 0,
            top: contentBounds.y,
            width: contentBounds.x,
            height: contentBounds.height
          }}
        />
      )}
      {/* Right area beside content */}
      {contentBounds.x + contentBounds.width < window.innerWidth && (
        <div
          className="absolute bg-black bg-opacity-60"
          style={{
            left: contentBounds.x + contentBounds.width,
            top: contentBounds.y,
            width: window.innerWidth - (contentBounds.x + contentBounds.width),
            height: contentBounds.height
          }}
        />
      )}

      {/* Control Buttons */}
      <div className="absolute top-4 right-4 flex gap-3">
        <button
          onClick={onCancel}
          disabled={isCapturing}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </button>
        <button
          onClick={handleCapture}
          disabled={isCapturing}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isCapturing ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Capturing...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Capture Selection
            </>
          )}
        </button>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg text-sm">
        Drag to move • Drag corners to resize • Click "Capture Selection" when ready
      </div>
    </div>,
    document.body
  );
};

export default PortionSelector;